import React from "react";
import { Input, Select, Button, Row, Col } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { QUICK_FACT_ICONS } from "../../../utils/quickFactIcons";

// Repeatable list of plain strings — used for highlights, important_info,
// cancellation_policy, exclusions, included_products, and quick-trip inclusions.
export const StringListEditor = ({ value = [], onChange, placeholder }) => {
  const items = value.length ? value : [""];

  const setItem = (idx, next) => {
    const copy = [...items];
    copy[idx] = next;
    onChange(copy);
  };

  const removeItem = (idx) => {
    const copy = items.filter((_, i) => i !== idx);
    onChange(copy.length ? copy : [""]);
  };

  const addItem = () => onChange([...items, ""]);

  return (
    <div>
      {items.map((item, idx) => (
        <Row key={idx} gutter={8} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <Input
              value={item}
              placeholder={placeholder}
              onChange={(e) => setItem(idx, e.target.value)}
            />
          </Col>
          <Col>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => removeItem(idx)}
              disabled={items.length === 1 && !item}
            />
          </Col>
        </Row>
      ))}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={addItem}>
        Add
      </Button>
    </div>
  );
};

// { label, description } rows — used for Package inclusions.
export const LabeledListEditor = ({ value = [], onChange }) => {
  const items = value.length ? value : [{ label: "", description: "" }];

  const setItem = (idx, patch) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };

  const removeItem = (idx) => {
    const copy = items.filter((_, i) => i !== idx);
    onChange(copy.length ? copy : [{ label: "", description: "" }]);
  };

  const addItem = () => onChange([...items, { label: "", description: "" }]);

  return (
    <div>
      {items.map((item, idx) => (
        <Row key={idx} gutter={8} style={{ marginBottom: 8 }}>
          <Col span={9}>
            <Input
              placeholder="Label"
              value={item.label}
              onChange={(e) => setItem(idx, { label: e.target.value })}
            />
          </Col>
          <Col span={12}>
            <Input
              placeholder="Description (optional)"
              maxLength={120}
              showCount
              value={item.description}
              onChange={(e) => setItem(idx, { description: e.target.value })}
            />
          </Col>
          <Col span={3}>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => removeItem(idx)}
              disabled={items.length === 1 && !item.label}
            />
          </Col>
        </Row>
      ))}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={addItem}>
        Add
      </Button>
    </div>
  );
};

// { icon, label, description } rows — used for quick_facts (both types).
export const QuickFactsEditor = ({ value = [], onChange }) => {
  const items = value.length
    ? value
    : [{ icon: "", label: "", description: "" }];

  const setItem = (idx, patch) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };

  const removeItem = (idx) => {
    const copy = items.filter((_, i) => i !== idx);
    onChange(copy.length ? copy : [{ icon: "", label: "", description: "" }]);
  };

  const addItem = () =>
    onChange([...items, { icon: "", label: "", description: "" }]);

  return (
    <div>
      {items.map((item, idx) => (
        <Row key={idx} gutter={8} style={{ marginBottom: 8 }}>
          <Col span={5}>
            <Select
              placeholder="Icon"
              style={{ width: "100%" }}
              value={item.icon || undefined}
              onChange={(icon) => setItem(idx, { icon })}
              options={QUICK_FACT_ICONS.map(({ value, label }) => ({
                value,
                label: (
                  <span>
                    <img
                      src={value}
                      alt=""
                      width={16}
                      height={16}
                      style={{ marginRight: 8, verticalAlign: "-3px" }}
                    />
                    {label}
                  </span>
                ),
              }))}
            />
          </Col>
          <Col span={7}>
            <Input
              placeholder="Label (e.g. Duration)"
              value={item.label}
              onChange={(e) => setItem(idx, { label: e.target.value })}
            />
          </Col>
          <Col span={9}>
            <Input
              placeholder="Description (e.g. 7 days, 6 nights)"
              value={item.description}
              onChange={(e) => setItem(idx, { description: e.target.value })}
            />
          </Col>
          <Col span={3}>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => removeItem(idx)}
              disabled={items.length === 1 && !item.label}
            />
          </Col>
        </Row>
      ))}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={addItem}>
        Add
      </Button>
    </div>
  );
};
